import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Bug,
  Send,
  Image,
  AlertCircle,
  Clock,
  CheckCircle,
  Loader2,
  Trash2,
  ChevronDown
} from 'lucide-react';
import { getApiBaseUrl } from '../lib/getApiUrl';

const API_URL = getApiBaseUrl();

type Category = 'Bug' | 'Feature Request' | 'Data Issue' | 'UI Problem' | 'Performance' | 'Other';
type Priority = 'Low' | 'Medium' | 'High' | 'Critical';
type IssueStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';

interface ReportedIssue {
  id: string;
  description: string;
  category: Category;
  priority: Priority;
  status: IssueStatus;
  pageUrl?: string;
  createdAt: string;
}

const CATEGORIES: Category[] = ['Bug', 'Feature Request', 'Data Issue', 'UI Problem', 'Performance', 'Other'];
const PRIORITIES: Priority[] = ['Low', 'Medium', 'High', 'Critical'];

const PRIORITY_COLORS: Record<Priority, string> = {
  Low: 'bg-gray-100 text-gray-700',
  Medium: 'bg-blue-100 text-blue-700',
  High: 'bg-orange-100 text-orange-700',
  Critical: 'bg-red-100 text-red-700',
};

const STATUS_COLORS: Record<IssueStatus, string> = {
  OPEN: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  RESOLVED: 'bg-green-100 text-green-700',
  CLOSED: 'bg-gray-100 text-gray-600',
};

const STATUS_LABELS: Record<IssueStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export function ReportIssuePage() {
  const { user } = useAuth();
  const { toast } = useToast();

  // Form state
  const [category, setCategory] = useState<Category>('Bug');
  const [priority, setPriority] = useState<Priority>('Medium');
  const [description, setDescription] = useState('');
  const [pageUrl, setPageUrl] = useState(window.location.href);
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Issues list state
  const [issues, setIssues] = useState<ReportedIssue[]>([]);
  const [isLoadingIssues, setIsLoadingIssues] = useState(true);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchIssues = useCallback(async () => {
    setIsLoadingIssues(true);
    try {
      const res = await fetch(`${API_URL}/issues?own=true`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setIssues(
          Array.isArray(data.data)
            ? data.data
            : Array.isArray(data.issues)
            ? data.issues
            : Array.isArray(data)
            ? data
            : []
        );
      }
    } catch {
      // silently fail — issues list is non-critical
    } finally {
      setIsLoadingIssues(false);
    }
  }, []);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid File', description: 'Please select an image file.', variant: 'destructive' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File Too Large', description: 'Screenshot must be under 5MB.', variant: 'destructive' });
      return;
    }

    setScreenshotFile(file);
    const reader = new FileReader();
    reader.onload = () => setScreenshotPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const removeScreenshot = () => {
    setScreenshotFile(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (description.trim().length < 10) {
      toast({ title: 'Validation Error', description: 'Description must be at least 10 characters.', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      let screenshotBase64: string | undefined;
      if (screenshotFile) {
        screenshotBase64 = await fileToBase64(screenshotFile);
      }

      const res = await fetch(`${API_URL}/issues`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          description: description.trim(),
          pageUrl,
          category,
          priority,
          screenshotBase64,
        }),
      });

      if (res.ok) {
        toast({ title: 'Issue Reported', description: 'Thank you! Your issue has been submitted.' });
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          setDescription('');
          setPageUrl(window.location.href);
          setCategory('Bug');
          setPriority('Medium');
          removeScreenshot();
        }, 2000);
        fetchIssues();
      } else {
        const err = await res.json().catch(() => null);
        toast({
          title: 'Submission Failed',
          description: err?.message || 'Could not submit issue. Please try again.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({ title: 'Network Error', description: 'Could not reach the server. Please try again.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const truncate = (text: string, max: number) =>
    text.length > max ? text.slice(0, max) + '...' : text;

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Bug className="h-8 w-8 text-red-500" />
            Report an Issue
          </h1>
          <p className="text-gray-600 mt-1">Tell us what's not working and we'll fix it.</p>
        </div>

        {/* Report Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-blue-600" />
                New Issue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Category + Priority row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Category */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Category</label>
                    <div className="relative">
                      <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value as Category)}
                        className="w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 pr-8 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Priority */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Priority</label>
                    <div className="relative">
                      <select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value as Priority)}
                        className="w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 pr-8 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        {PRIORITIES.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={6}
                    placeholder="Describe the issue in detail. What happened? What did you expect to happen?"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-vertical"
                    required
                    minLength={10}
                  />
                  <p className={`text-sm font-medium ${description.trim().length < 10 ? 'text-red-500 font-bold' : 'text-green-600'}`}>
                    {description.trim().length < 10
                      ? `${description.trim().length}/10 — need ${10 - description.trim().length} more character${10 - description.trim().length !== 1 ? 's' : ''}`
                      : `${description.trim().length}/10 \u2713 Ready to submit`}
                  </p>
                </div>

                {/* Page URL */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Page URL (auto-filled)</label>
                  <Input
                    value={pageUrl}
                    onChange={(e) => setPageUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                {/* Screenshot */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    Attach Screenshot
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="screenshot-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Image className="h-4 w-4 mr-2" />
                      Choose Image
                    </Button>
                    {screenshotFile && (
                      <span className="text-sm text-gray-500">{screenshotFile.name}</span>
                    )}
                  </div>
                  {screenshotPreview && (
                    <div className="relative mt-2 inline-block">
                      <img
                        src={screenshotPreview}
                        alt="Screenshot preview"
                        className="max-h-48 rounded-md border border-gray-200 shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={removeScreenshot}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Submit */}
                <div className="pt-2 space-y-2">
                  {showSuccess && (
                    <div className="flex items-center gap-2 text-green-600 font-medium text-sm bg-green-50 border border-green-200 rounded-md px-3 py-2">
                      <CheckCircle className="h-4 w-4" />
                      Issue submitted!
                    </div>
                  )}
                  <div title={description.trim().length < 10 ? 'Description must be at least 10 characters' : undefined}>
                    <Button
                      type="submit"
                      disabled={isSubmitting || description.trim().length < 10}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {isSubmitting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      {isSubmitting ? 'Submitting...' : 'Submit Issue'}
                    </Button>
                  </div>
                  {description.trim().length < 10 && !showSuccess && (
                    <p className="text-xs text-gray-500">Write at least 10 characters to submit</p>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* My Reported Issues */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-gray-600" />
                My Reported Issues
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingIssues ? (
                <div className="flex items-center justify-center py-8 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Loading issues...
                </div>
              ) : issues.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <CheckCircle className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p>No issues reported yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-gray-500">
                        <th className="pb-3 pr-4 font-medium">Date</th>
                        <th className="pb-3 pr-4 font-medium">Description</th>
                        <th className="pb-3 pr-4 font-medium">Category</th>
                        <th className="pb-3 pr-4 font-medium">Priority</th>
                        <th className="pb-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {issues.map((issue) => (
                        <tr key={issue.id} className="hover:bg-gray-50">
                          <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">
                            {formatDate(issue.createdAt)}
                          </td>
                          <td className="py-3 pr-4 text-gray-800 max-w-xs">
                            {truncate(issue.description, 80)}
                          </td>
                          <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">
                            {issue.category}
                          </td>
                          <td className="py-3 pr-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[issue.priority] || 'bg-gray-100 text-gray-700'}`}>
                              {issue.priority}
                            </span>
                          </td>
                          <td className="py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[issue.status] || 'bg-gray-100 text-gray-600'}`}>
                              {STATUS_LABELS[issue.status] || issue.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
